import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { Html5QrcodeScanner } from "html5-qrcode";
import { Table, Badge, Spinner } from "react-bootstrap";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import UserContext from "../../context/UserContext";

const AttendanceSystem = () => {
  const { user } = useContext(UserContext);

  const getElapsedSeconds = (startTime) => {
    if (!startTime) return 0;
    const elapsed = Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
    return elapsed > 0 ? elapsed : 0;
  };

  const [isStarted, setIsStarted] = useState(() => !!localStorage.getItem("workStartTime"));
  const [seconds, setSeconds] = useState(() => getElapsedSeconds(localStorage.getItem("workStartTime")));
  const [loading, setLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [currentUserName, setCurrentUserName] = useState("");
  const [qrCodeValue, setQrCodeValue] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [historyLogs, setHistoryLogs] = useState([]);

  const timerRef = useRef(null);

  const startTimer = (startTimeString) => {
    if (timerRef.current) clearInterval(timerRef.current);
    const startTime = new Date(startTimeString).getTime();
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setSeconds(elapsed > 0 ? elapsed : 0);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setSeconds(0);
    localStorage.removeItem("workStartTime");
  };

  const fetchUserLogs = useCallback(async () => {
    const rawId = user?.id || user?.userId || localStorage.getItem("userId");
    if (!rawId) return;
    setLogsLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axiosClient.get(`${API_ENDPOINTS.attendance}/logs`, {
        params: { userId: rawId, startDate: today, endDate: today }
      });
      setHistoryLogs(Array.isArray(response.data.attendance) ? response.data.attendance : []);
    } catch (error) {
      console.error("Fetch logs failed:", error);
    } finally {
      setLogsLoading(false);
    }
  }, [user]);

  const syncStatus = async () => {
    const rawId = user?.id || user?.userId || localStorage.getItem("userId");
    if (!rawId) return;
    try {
      const response = await axiosClient.get(`${API_ENDPOINTS.attendance}/current-status/${rawId}`);
      const { hasOpenWork, startTime } = response.data;
      if (hasOpenWork && startTime) {
        if (!isStarted || localStorage.getItem("workStartTime") !== startTime) {
          setIsStarted(true);
          localStorage.setItem("workStartTime", startTime);
          startTimer(startTime);
        }
      } else {
        if (isStarted) {
          setIsStarted(false);
          stopTimer();
        }
      }
    } catch (error) { console.error("Sync failed:", error); }
  };

  useEffect(() => {
    const displayName = user?.employeeName || user?.userName || localStorage.getItem("userName");
    setCurrentUserName(displayName || "N/A");
    const savedStart = localStorage.getItem("workStartTime");
    if (savedStart) startTimer(savedStart);
    syncStatus();
    fetchUserLogs();
    const syncInterval = setInterval(() => { syncStatus(); fetchUserLogs(); }, 10000);
    return () => { clearInterval(syncInterval); if (timerRef.current) clearInterval(timerRef.current); };
  }, [user, fetchUserLogs]);

  useEffect(() => {
    let scanner = null;
    if (showScanner) {
      scanner = new Html5QrcodeScanner("reader", { fps: 10, qrbox: { width: 250, height: 250 } });
      scanner.render((text) => { setQrCodeValue(text); setShowScanner(false); scanner.clear(); }, () => {});
    }
    return () => { if (scanner) scanner.clear(); };
  }, [showScanner]);

  const getCurrentLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error("Geolocation is not supported by this browser!"));
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => {
          // Detailed English GPS errors
          switch (err.code) {
            case err.PERMISSION_DENIED:
              reject(new Error("Location access denied. Please enable GPS/Location permissions on your device/browser."));
              break;
            case err.POSITION_UNAVAILABLE:
              reject(new Error("Location information is unavailable. Please check your network or GPS signal."));
              break;
            case err.TIMEOUT:
              reject(new Error("The request to get user location timed out. Please try again."));
              break;
            default:
              reject(new Error(`Location error: ${err.message}`));
          }
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });

  const saveAttendanceLog = async (actionType) => {
    const rawId = user?.id || user?.userId || localStorage.getItem("userId");
    if (!rawId) return alert("User ID not found! Invalid session.");
    setLoading(true);
    
    try {
      // 1. Get GPS Location
      const location = await getCurrentLocation();
      
      const payload = {
        userId: String(rawId),
        actionName: actionType,
        latitude: parseFloat(location.lat),
        longitude: parseFloat(location.lng),
        timestamp: new Date().toISOString(),
        note: `Web Interface - ${actionType}`,
        scannedQRCode: qrCodeValue,
      };
      
      // 2. Post to API
      await axiosClient.post(`${API_ENDPOINTS.attendance}/save-log`, payload);
      
      if (actionType === "LOGOUT") {
        setQrCodeValue("");
        setIsStarted(false);
        stopTimer();
      } else {
        setIsStarted(true);
        await syncStatus();
      }
      await fetchUserLogs();
      alert("Attendance recorded successfully!");
      
    } catch (error) {
      console.error("Attendance Error:", error);
      
      // 3. Parse English error details
      let errorMessage = "An unknown error occurred.";

      if (error.response) {
        // Backend API error (e.g., 400, 403, 500)
        const serverData = error.response.data;
        errorMessage = serverData?.message || serverData?.error || `Server Error (${error.response.status})`;
      } else if (error.request) {
        // Network or Server down error
        errorMessage = "Cannot connect to the server. Please check your internet connection.";
      } else if (error.message) {
        // Client-side or GPS rejection error
        errorMessage = error.message;
      }

      alert(`Attendance Failed!\nDetails: ${errorMessage}`);
      
    } finally {
      setLoading(false);
    }
  };

  // const saveAttendanceLog = async (actionType) => {
  //   const rawId = user?.id || user?.userId || localStorage.getItem("userId");
  //   if (!rawId) return alert("User ID not found!");
  //   setLoading(true);
  //   try {
  //     const location = await getCurrentLocation();
  //     const payload = {
  //       userId: String(rawId),
  //       actionName: actionType,
  //       latitude: parseFloat(location.lat),
  //       longitude: parseFloat(location.lng),
  //       timestamp: new Date().toISOString(),
  //       note: `Web Interface - ${actionType}`,
  //       scannedQRCode: qrCodeValue,
  //     };
  //     await axiosClient.post(`${API_ENDPOINTS.attendance}/save-log`, payload);
      
  //     if (actionType === "LOGOUT") {
  //       setQrCodeValue("");
  //       setIsStarted(false);
  //       stopTimer();
  //     } else {
  //       setIsStarted(true);
  //       await syncStatus();
  //     }
  //     await fetchUserLogs();
  //     alert("Attendance recorded successfully!");
  //   } catch (error) {
  //     console.error(error);
  //     alert("Failed to save attendance! Action not completed.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const formatTime = (sec) => {
    const h = Math.floor(sec / 3600).toString().padStart(2, "0");
    const m = Math.floor((sec % 3600) / 60).toString().padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  return (
    <div style={{ maxWidth: "800px", margin: "auto" }}>
      {/* CARD CHÍNH */}
      <div className="card shadow-lg border-0 mb-4" style={{ borderRadius: "20px", maxWidth: "420px", margin: "auto" }}>
        <div className="card-header bg-primary text-white text-center py-3" style={{ borderRadius: "20px 20px 0 0" }}>
          <h6 className="m-0 fw-bold text-uppercase"><i className="fas fa-user-circle me-2"></i> USER: {currentUserName}</h6>
        </div>
        <div className="card-body p-4 text-center">
          <div className="mb-4">
            <div className="small text-muted text-uppercase fw-bold mb-1">Work Duration</div>
            <h2 className="display-4 fw-bold text-dark" style={{ fontFamily: "monospace" }}>{formatTime(seconds)}</h2>
          </div>

          <div className="mb-4">
            <button className="btn btn-outline-primary btn-sm mb-2 w-100 fw-bold" onClick={() => setShowScanner(!showScanner)}>
              <i className="fas fa-qrcode me-2"></i> {showScanner ? "Close Scanner" : "Scan Work Order / Item Form"}
            </button>
            {showScanner && <div id="reader" className="mb-2 border rounded"></div>}
            <div className="input-group">
              <span className="input-group-text bg-light"><i className="fas fa-link text-muted"></i></span>
              <input className="form-control bg-light text-center fw-bold" value={qrCodeValue} readOnly placeholder="Scanned Code will appear here" />
            </div>
            <div className="small text-muted mt-1 text-start">* This code will be sent when you Start/Stop work.</div>
          </div>

          <button className={`btn btn-lg w-100 py-3 fw-bold ${isStarted ? "btn-danger" : "btn-success"}`} 
                  onClick={() => saveAttendanceLog(isStarted ? "LOGOUT" : "LOGIN")} disabled={loading}>
            {loading ? <Spinner size="sm" /> : (isStarted ? "Stop Working Hours" : "Start Working Hours")}
          </button>
        </div>
        <div className="card-footer bg-light text-muted small text-center py-2">
          GPS & QR Verification • {new Date().toLocaleDateString()}
        </div>
      </div>

      {/* BẢNG LỊCH SỬ */}
      <div className="card shadow-lg border-0" style={{ borderRadius: "20px" }}>
        <div className="card-body">
          <h6 className="fw-bold text-muted mb-3"><i className="fas fa-history me-2"></i> ATTENDANCE HISTORY (TODAY)</h6>
          <div className="table-responsive">
            <Table bordered hover size="sm" className="text-center small">
              <thead className="bg-primary text-white">
                <tr><th>DATE</th><th>ACTION</th><th>TIME</th><th>HOURS</th><th>LOCATION</th><th>LAT</th><th>LNG</th></tr>
              </thead>
              <tbody>
                {logsLoading ? <tr><td colSpan="7">Loading...</td></tr> : historyLogs.map((log, i) => (
                  <tr key={i}>
                    <td>{new Date(log.createdAt).toLocaleDateString()}</td>
                    <td><Badge bg={log.actionType?.includes("IN") ? "primary" : "secondary"}>{log.actionType}</Badge></td>
                    <td>{new Date(log.createdAt).toLocaleTimeString()}</td>
                    <td>{log.durationHours?.toFixed(1) || "-"}</td>
                    <td>{log.workLocation || "N/A"}</td>
                    <td>{log.latitude?.toFixed(2)}</td>
                    <td>{log.longitude?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceSystem;